import React from "react";
import { ToastContainer, cssTransition, toast as toastify } from "react-toastify";
import "../styles/toast.css";

/**
 * Youro's toasts: one style for success, info and error, and one container for
 * the whole app. Import `toast` from here, never from react-toastify directly.
 *
 * Why one container. react-toastify hands every toast to every ToastContainer
 * that is mounted, so a screen that mounts two shows each toast twice. The app
 * had 21 of them, one per screen, and two screens nested them: provider Patient
 * Details renders Orders, and Note Templates renders the Add Template popup,
 * each with a container of its own. The opposite case was broken too — a screen
 * with no container, such as booking, queued its toasts until some later screen
 * mounted one, so they surfaced on the wrong page. <YouroToaster /> is mounted
 * once, in App, and nothing else should mount a container.
 *
 * Why the toastId. Nothing stops a form being submitted twice while the first
 * request is still out — a double-click, or Enter and then the button — and each
 * response raised its own identical toast. Keying a toast on its type and text
 * means an identical one already on screen is left alone rather than stacked.
 * That hides the symptom only: the duplicate request still goes out. Stopping it
 * means disabling the submit while a request is in flight, at each form.
 */

// Enter and exit are defined in toast.css: in from the right over 250ms, ease-out.
const Slide = cssTransition({
  enter: "youro-toast-enter",
  exit: "youro-toast-exit",
  collapseDuration: 200,
});

// Errors ask the reader to do something, so they stay up long enough to act on.
const AUTO_CLOSE_MS = { success: 5000, info: 5000, error: 8000 };

const withDefaults = (type, content, options = {}) => ({
  toastId: typeof content === "string" ? `${type}:${content}` : undefined,
  autoClose: AUTO_CLOSE_MS[type],
  // Errors interrupt; confirmations and updates wait their turn.
  role: type === "error" ? "alert" : "status",
  ...options,
});

export const toast = Object.assign(
  (content, options) => toastify(content, withDefaults("info", content, options)),
  toastify,
  {
    success: (content, options) => toastify.success(content, withDefaults("success", content, options)),
    info: (content, options) => toastify.info(content, withDefaults("info", content, options)),
    error: (content, options) => toastify.error(content, withDefaults("error", content, options)),
  }
);
// There is no warning style; anything raised as one is shown as info.
toast.warn = toast.info;
toast.warning = toast.info;

const ICON_PATHS = {
  // Checkmark, drawn in Charcoal on a Sage disc. Sage straight onto Mint is 2.0:1
  // and fails the 3:1 WCAG floor for icons (DESIGN_SYSTEM.md §2 lists Sage on
  // Mint as forbidden). Charcoal on Sage is 5.2:1, so the disc can stay Sage.
  success: <path d="M7.5 12.5l3 3 6-6.5" fill="none" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />,
  info: (
    <>
      <path d="M12 11v5.5" fill="none" strokeWidth="2.25" strokeLinecap="round" />
      <circle cx="12" cy="7.75" r="1.35" stroke="none" />
    </>
  ),
  error: (
    <>
      <path d="M12 6.75v6.5" fill="none" strokeWidth="2.25" strokeLinecap="round" />
      <circle cx="12" cy="16.9" r="1.35" stroke="none" />
    </>
  ),
};

const ToastIcon = ({ type }) => {
  const kind = ICON_PATHS[type] ? type : "info";
  return (
    <svg className={`youro-toast-icon youro-toast-icon--${kind}`} viewBox="0 0 24 24" aria-hidden="true">
      <circle className="youro-toast-icon-disc" cx="12" cy="12" r="11" />
      <g className="youro-toast-icon-glyph">{ICON_PATHS[kind]}</g>
    </svg>
  );
};

const CloseButton = ({ closeToast }) => (
  <button type="button" className="youro-toast-close" onClick={closeToast} aria-label="Dismiss notification">
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  </button>
);

export const YouroToaster = () => (
  <ToastContainer
    className="youro-toasts"
    position="top-right"
    transition={Slide}
    icon={({ type }) => <ToastIcon type={type} />}
    closeButton={CloseButton}
    hideProgressBar
    newestOnTop
    closeOnClick={false}
    pauseOnHover
    pauseOnFocusLoss
    draggable
    limit={3}
  />
);
